/**
 * Duel System TypeScript Types
 *
 * Defines all types for WebSocket messages, duel state, and related data structures.
 */

import { Pokemon, Player } from "prisma-layer";
import { DuelPokemon } from "types/duel-dynamodb";
import { BattleCommand } from "types/battle-engine/commands";
import { LiveBattleServerMessage } from "types/battle-engine/live-messages";

// ============================================================================
// Duel System Constants
// ============================================================================

export const MAX_DAILY_REWARDED_DUELS = 6;

// ============================================================================
// WebSocket Message Types - Client to Server
// ============================================================================

export interface PlayerActionMessage {
  action: "player_action";
  payload: {
    duel_id: string;
    step_index: number;
    action_type: "pick" | "ban";
    pokemon_ids: number[];
  };
}

export interface VisualAckMessage {
  action: "visual_ack";
  payload: {
    duel_id: string;
    round_index: number;
  };
}

export interface GetStateMessage {
  action: "get_state";
  payload: {
    duel_id: string;
  };
}

/** Draft-only, once-per-round hand reroll (core-pvp-revamp §4). */
export interface RerollHandMessage {
  action: "reroll";
  payload: {
    duel_id: string;
  };
}

export interface HeartbeatMessage {
  action: "heartbeat";
}

/** Live battle room command (F3) — forwarded as-is to the Fargate battle-server. */
export interface BattleCommandMessage {
  action: "battle_command";
  payload: {
    duel_id: string;
    command: BattleCommand;
  };
}

export type ClientMessage =
  | PlayerActionMessage
  | VisualAckMessage
  | GetStateMessage
  | HeartbeatMessage
  | BattleCommandMessage
  | RerollHandMessage;

// ============================================================================
// WebSocket Message Types - Server to Client
// ============================================================================

export interface DuelInitializedMessage {
  type: "duel_initialized";
  payload: {
    duel_id: string;
    player_role: "player_a" | "player_b";
    opponent: Pick<Player, "id" | "username" | "country">;
    duel_draft_type: string;
    your_team: DuelPokemon[];
    your_team_name: string;
    opponent_team: DuelPokemon[];
    opponent_team_name: string;
    your_picks: number[];
    opponent_picks: number[];
    your_banned_picks: number[];
    opponent_banned_picks: number[];
    current_round_index: number;
    current_step_index: number;
    duel_status: string;
    map_id: string;
    your_score: number;
    opponent_score: number;
    max_rounds: number;
    /** Own private draft hand + reroll flag — never sent for the opponent. */
    your_hand: string[];
    your_reroll_used: boolean;
    /** Opponent's full 16-item deck — public (core-pvp-revamp §4). */
    opponent_items: string[];
  };
}

/** Sent only to the requesting player after a successful reroll (core-pvp-revamp §4). */
export interface HandUpdatedMessage {
  type: "hand_updated";
  payload: {
    your_hand: string[];
    your_reroll_used: boolean;
  };
}

export interface DuelRoundStartedMessage {
  type: "duel_round_started";
  payload: {
    round_index: number;
    /** Own private draft hand for the new round — never sent for the opponent. */
    your_hand: string[];
    your_reroll_used: boolean;
  };
}

export interface StepStartedMessage {
  type: "step_started";
  payload: {
    round_index: number;
    step_index: number;
    step_type: "pick" | "ban";
    duration_seconds: number;
    picks_count: number;
    available_options: number[];
    started_at: string;
    your_remaining_bonus_time: number;
    time_remaining: number;
    already_submitted: boolean;
    your_picks: number[];
  };
}

export interface StepResultsMessage {
  type: "step_results";
  payload: {
    step_index: number;
    step_type: "pick" | "ban";
    your_choices: number[];
    opponent_choices: number[];
    your_updated_available: number[];
    opponent_updated_available: number[];
    your_banned_picks: number[];
    your_picks: number[];
    opponent_banned_picks: number[];
    opponent_picks: number[];
  };
}

export interface BattleStartingMessage {
  type: "battle_starting";
  payload: {
    your_team: number[];
    opponent_team: number[];
  };
}

export interface BattleStartedMessage {
  type: "battle_started";
  payload: {
    round_index: number;
    your_team: number[];
    opponent_team: number[];
    /** Present for finished/legacy battles (S3 replay). Absent for a live room — the client streams `battle_actions` instead. */
    battle_log_url?: string;
    /** Present for a live room, so the client can spawn its own scene without draft continuity. */
    battle_id?: string;
    your_score: number;
    opponent_score: number;
  };
}

export interface DuelFinishedMessage {
  type: "duel_finished";
  payload: {
    winner: "you" | "opponent" | "draw";
    final_score_you: number;
    final_score_opponent: number;
    total_battles: number;
    quit?: boolean; // If duel is finished due to quit by one of the players
    quitter?: "player_a" | "player_b"; // The player who quit
    /** Present for non-competitive endings (forfeit/timeout/error) to explain why. */
    reason?: "grace_period_exceeded" | "connection_timeout" | "duel_failed";
    rating: number;
    new_rating: number;

    coins_gain: number;
    shards_gain: number;
    rewarded_duels_remaining: number;
  };
}

export interface ErrorMessage {
  type: "error";
  payload: {
    code: string;
    message: string;
  };
}

export interface ConnectionStatusMessage {
  type: "connection_status";
  payload: {
    status: "connected" | "reconnected" | "opponent_disconnected";
    grace_period_remaining_seconds?: number;
  };
}

export interface SubmissionIgnoredMessage {
  type: "submission_ignored";
  payload: {
    reason: string;
  };
}

/**
 * Sent right after a battle finishes (before the next round's
 * `battle_started`/the final `duel_finished`) so the post-battle transition
 * screen can show the up-to-date score instead of the pre-battle one.
 */
export interface RoundScoresMessage {
  type: "round_scores";
  payload: {
    round_index: number;
    your_score: number;
    opponent_score: number;
    round_winner: "player_a" | "player_b" | "draw";
  };
}

export type ServerMessage =
  | DuelInitializedMessage
  | DuelRoundStartedMessage
  | StepStartedMessage
  | StepResultsMessage
  | BattleStartingMessage
  | BattleStartedMessage
  | DuelFinishedMessage
  | ErrorMessage
  | ConnectionStatusMessage
  | SubmissionIgnoredMessage
  | HandUpdatedMessage
  | RoundScoresMessage
  | LiveBattleServerMessage;

// ============================================================================
// Duel State Types
// ============================================================================

export type DuelStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "initializing"
  | "waiting_for_step"
  | "in_step"
  | "waiting_for_opponent_pick"
  | "step_completed"
  | "battle_starting"
  | "in_battle"
  | "battle_completed"
  | "new_round_starting"
  | "finished"
  | "error"
  | "disconnected";

export interface DuelStepState {
  round_index: number;
  step_index: number;
  step_type: "pick" | "ban";
  duration_seconds: number;
  picks_count: number;
  available_options: number[];
  started_at: string | null;
  your_choices: number[];
  opponent_choices: number[];
  time_remaining?: number;
  bonus_time_remaining?: number;
  your_remaining_bonus_time: number;
}

/** HUD-facing live-battle state (F3): item hand, cooldown, focus. Reset on every `battle_started`. */
export interface LiveBattleHudState {
  hand: string[];
  item_cooldown_remaining: number;
  focus_target_id?: number;
  /** Last `item_played`/`item_rejected` outcome, for the played-card banner. Cleared by the HUD after showing it. */
  last_item_notice?: {
    notice_type: "item_played" | "item_rejected";
    item_card_key?: string;
    player_id: string;
  };
}

export interface DuelBattleState {
  round_index: number;
  your_team: number[];
  opponent_team: number[];
  /** Legacy/finished battles only. */
  battle_log_url?: string;
  /** Live battles only — id of the Fargate room to stream from. */
  battle_id?: string;
  winner?: "player_a" | "player_b";
}

export interface DuelState {
  status: DuelStatus;
  duel_id: string | null;
  player_role: "player_a" | "player_b" | null;

  // Player and team info
  opponent_player: Pick<Player, "id" | "username" | "country"> | null;
  your_team: DuelPokemon[];
  your_team_name: string | null;
  opponent_team: DuelPokemon[];
  opponent_team_name: string | null;
  your_available_pokemon: number[];
  opponent_available_pokemon: number[];

  // Round-specific pick/ban tracking
  your_banned_picks: number[];
  your_picks: number[];
  opponent_banned_picks: number[];
  opponent_picks: number[];

  // Current step state
  current_step: DuelStepState | null;

  // Current battle state
  current_battle: DuelBattleState | null;
  last_battle: DuelBattleState | null;
  /** Live-room HUD state (F3) — null while not in a live battle. */
  live_battle: LiveBattleHudState | null;

  // Scores
  your_score: number;
  opponent_score: number;

  // Finish data
  finish_reason?:
    | "player_a_quit"
    | "player_b_quit"
    | "score"
    | "error"
    | "cancelled";
  result?: {
    old_rating: number;
    new_rating: number;
    winner: "you" | "opponent" | "draw";
    coins_gain: number;
    shards_gain: number;
    rewarded_duels_remaining: number;
  };

  // Current round index (synced from server)
  current_round_index?: number;

  // Series length (Bo3, Bo5, etc.), synced from server
  max_rounds?: number;

  // Own private draft hand (never populated for the opponent)
  your_hand: string[];
  your_reroll_used: boolean;
  // Opponent's full 16-item deck — public
  opponent_items: string[];

  // Draft type info
  duel_draft_type: string | null;

  // Map info
  map_id: string | null;

  // Error info
  error: {
    code: string;
    message: string;
  } | null;

  // Connection info
  connection_status: "connected" | "disconnected" | "reconnecting";
}

// ============================================================================
// WebSocket Service Types
// ============================================================================

export type WebSocketConnectionStatus =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting";

export interface WebSocketConfig {
  url: string;
  duel_id: string;
  player_id: string;
  token: string;
  reconnectAttempts?: number;
  reconnectDelay?: number;
  heartbeatInterval?: number;
}

export type MessageHandler = (message: ServerMessage) => void;
export type ConnectionHandler = (status: WebSocketConnectionStatus) => void;
export type ErrorHandler = (error: Error) => void;

// ============================================================================
// Helper Types
// ============================================================================

export interface PokemonSelection {
  pokemon_id: number;
  pokemon: Pokemon;
}

export interface DuelSummary {
  duel_id: string;
  winner: "you" | "opponent" | "draw";
  final_score_you: number;
  final_score_opponent: number;
  total_battles: number;
  opponent_name: string;
}
